<template>
  <b-container fluid>
    <AuditFilters
      :is-loading="auditFilters.isLoading"
      @apply-filters="handleFilterUpdate"
      @update-filters="handleFilters"
    />

    <div
      v-if="auditsTable.bigqueryStats && !auditsTable.isLoading"
      class="d-flex align-items-center mb-3 justify-content-end"
    >
      <small class="text-muted">
        Processed: {{ formatBytes(auditsTable.bigqueryStats.bytesProcessed) }} |
        Billed: {{ formatBytes(auditsTable.bigqueryStats.bytesBilled) }}
        {{ auditsTable.bigqueryStats.cacheHit ? '(cached)' : '' }}
      </small>
      <b-icon-info-circle
        v-b-tooltip.hover.left
        :title="bigQueryTooltip"
        variant="info"
        class="ml-2"
      />
    </div>

    <AuditTable
      :is-loading="auditsTable.isLoading"
      :items="auditsTable.items"
      :pagination="auditsTable.pagination"
      @page-changed="handlePageChange"
      @show-log-details="fetchLogDetails"
      @show-sublog-details="fetchSublogDiffData"
      @load-transaction-logs="fetchTransactionLogs"
    />

    <AuditLogDetails
      :is-visible="logDetailsModal.isVisible"
      :log-details="logDetailsModal.data"
      @hide="logDetailsModal.isVisible = false"
      @requested-diff="fetchDiffData"
    />

    <AuditSublogDiff
      :is-visible="sublogDiffModal.isVisible"
      :is-loading="sublogDiffModal.isLoading"
      :has-error="sublogDiffModal.hasError"
      :payload-green="sublogDiffModal.data.payload1"
      :payload-red="sublogDiffModal.data.payload2"
      @hidden="hiddenSublogDiffModal"
    />
  </b-container>
</template>

<script>
import axiosLoggingClient from '../plugins/axios';
import AuditTable from '../components/AuditTable.vue';
import AuditFilters from '../components/AuditFilters.vue';
import AuditLogDetails from '../components/AuditLogDetails.vue';
import AuditSublogDiff from '../components/AuditSublogDiff.vue';

export default {
  name: 'Audits',
  components: {
    AuditFilters,
    AuditTable,
    AuditLogDetails,
    AuditSublogDiff,
  },
  data: () => ({
    filters: {},
    auditFilters: {
      isLoading: false,
    },
    auditsTable: {
      isLoading: true,
      items: [],
      pagination: {
        page: 1,
        pageSize: 10,
        total: 0,
      },
      bigqueryStats: {
        bytesBilled: 0,
        bytesProcessed: 0,
        cacheHit: false,
      },
    },
    logDetailsModal: {
      isVisible: false,
      data: {},
    },
    sublogDiffModal: {
      isVisible: false,
      isLoading: true,
      hasError: false,
      data: {
        payload1: '',
        payload2: '',
      },
    },
  }),
  methods: {
    searchAudits(params) {
      this.auditFilters.isLoading = true;
      this.auditsTable.isLoading = true;
      this.auditsTable.items.splice(0, this.auditsTable.items.length);

      return axiosLoggingClient
        .get('/v3/logs', { params })
        .then((response) => {
          response.data.audits.forEach((audit) => {
            this.auditsTable.items.push({
              ...audit,
              transactionLogs: {
                isLoading: true,
                items: [],
                pagination: {
                  page: 1,
                  pageSize: 10,
                  total: 0,
                },
              },
              diffData: {
                isLoading: false,
                data: { req: [], res: [] },
              },
            });
          });

          const { bigquery_stats: bigqueryStats, ...restMetadata } = response.data?.metadata || {};
          this.auditsTable.pagination.total = restMetadata.total_logs;
          this.auditsTable.bigqueryStats = {
            bytesBilled: bigqueryStats.bytes_billed,
            bytesProcessed: bigqueryStats.bytes_processed,
            cacheHit: bigqueryStats.cache_hit,
          };
        })
        .catch((error) => {
          // eslint-disable-next-line no-console
          console.error(error);
          this.$noty.error('Error fetching audits');
        })
        .finally(() => {
          this.auditFilters.isLoading = false;
          this.auditsTable.isLoading = false;
        });
    },
    fetchLogDetails(item) {
      Object.keys(this.logDetailsModal.data).forEach((key) => {
        this.$delete(this.logDetailsModal.data, key);
      });

      this.logDetailsModal.data.isLoading = true;

      return axiosLoggingClient
        .get(`/v3/logs/details/${item.transaction_id}`, {
          params: { createdAt: item.created_at },
        })
        .then((response) => {
          Object.assign(this.logDetailsModal.data, item);
          this.logDetailsModal.data = {
            ...this.logDetailsModal.data,
            ...response.data.details,
          };
          this.logDetailsModal.isVisible = true;
        })
        .catch((error) => {
          // eslint-disable-next-line no-console
          console.error(error);
          this.$noty.error('Error fetching audit');
        });
    },
    fetchTransactionLogs(transactionID, params) {
      this.auditsTable.items.forEach((item) => {
        if (item.transaction_id === transactionID) {
          item.transactionLogs.isLoading = true;
        }
      });
      return axiosLoggingClient
        .get(`/v3/logs/${transactionID}`, { params })
        .then((response) => {
          const foundTransaction = this.auditsTable.items.some((item) => {
            if (item.transaction_id === transactionID) {
              item.transactionLogs.items = response.data.audits;
              item.transactionLogs.isLoading = false;
              item.transactionLogs.pagination.total = response.data.metadata.total_logs;
              item.transactionLogs.pagination.page = response.data.metadata.page;
              item.transactionLogs.pagination.pageSize = response.data.metadata.page_size;
              return true;
            }

            return false;
          });

          if (!foundTransaction) {
            this.$noty.error(`Error trying to find logs for transaction ${transactionID}`);
          }
        })
        .catch((error) => {
          // eslint-disable-next-line no-console
          console.error(error);
          this.$noty.error('Error fetching audits');
        })
        .finally(() => {
          this.auditsTable.isLoading = false;
        });
    },
    fetchDiffData(logUUID) {
      const diffingLog = this.auditsTable.items.find((i) => i.uuid === logUUID);

      if (!diffingLog) {
        const errMsg = `Error trying to fetch diff data for log '${logUUID}'`;
        console.error(errMsg); // eslint-disable-line no-console
        this.$noty.error(errMsg);
        return Promise.reject(errMsg);
      }

      if (diffingLog.diffData.isLoading || diffingLog.diffData.data.req.length > 0) {
        return Promise.resolve();
      }

      diffingLog.diffData.isLoading = true;

      return axiosLoggingClient
        .get('/v3/logs/diff', {
          params: {
            completed_at: diffingLog.completed_at,
            diff_uuid: diffingLog.diff_uuid,
            entity_id: diffingLog.entity_id,
          },
        })
        .then((response) => {
          const [lag, lead] = response.data.audits;
          diffingLog.diffData.data = {
            res: [lead.response_payload, lag.response_payload],
          };
        })
        .catch((error) => {
          console.error(error); // eslint-disable-line no-console
          this.$noty.error('Error fetching diff data');
        })
        .finally(() => {
          diffingLog.diffData.isLoading = false;
        });
    },
    fetchSublogDiffData(sublogData) {
      this.sublogDiffModal.isVisible = true;
      this.sublogDiffModal.isLoading = true;

      return axiosLoggingClient
        .get('/v3/logs/diff', {
          params: {
            completed_at: sublogData.completed_at,
            diff_uuid: sublogData.diff_uuid,
            entity_id: sublogData.entity_id,
          },
        })
        .then((response) => {
          const [lag, lead] = response.data.audits;
          this.sublogDiffModal.data.payload1 = lead.response_payload;
          this.sublogDiffModal.data.payload2 = lag.response_payload;
        })
        .catch((error) => {
          this.sublogDiffModal.hasError = true;
          console.error(error); // eslint-disable-line no-console
          this.$noty.error('Error fetching sub-log diff data');
        })
        .finally(() => {
          this.sublogDiffModal.isLoading = false;
        });
    },
    handleFilterUpdate(updatedFilters) {
      Object.keys(this.filters).forEach((key) => {
        if (!(key in updatedFilters)) {
          this.$delete(this.filters, key);
        }
      });
      Object.assign(this.filters, updatedFilters);

      return this.handlePageChange(1);
    },
    handlePageChange(updatedPage) {
      if (updatedPage) {
        this.auditsTable.pagination.page = updatedPage;
      }

      return this.searchAudits({
        page: this.auditsTable.pagination.page,
        page_size: this.auditsTable.pagination.pageSize,
        ...this.filters,
      });
    },
    handleFilters(filters) {
      this.filters = filters;
    },
    hiddenSublogDiffModal() {
      this.sublogDiffModal.isVisible = false;
      this.sublogDiffModal.isLoading = true;
      this.sublogDiffModal.hasError = false;
      this.sublogDiffModal.data.payload1 = '';
      this.sublogDiffModal.data.payload2 = '';
    },
    formatBytes(bytes) {
      if (bytes === 0) return '0 Bytes';

      const k = 1024;
      const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));

      return `${parseFloat((bytes / k ** i).toFixed(2))} ${sizes[i]}`;
    },
  },
};
</script>
